import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import {
  Euro,
  ShoppingCart,
  UserPlus,
  Eye,
  Heart,
  Bot as BotIcon,
  AlertCircle,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { Section } from '@/components/Section';
import type { ClientRow } from '@/hooks/useClient';

export function DashboardPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();
  const { client } = useOutletContext<{ client: ClientRow }>();

  const kpisQuery = useQuery({
    queryKey: ['kpis', slug],
    queryFn: () => api.getKpis(slug!, () => getToken()),
    refetchInterval: 60_000,
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Vue d'ensemble</h1>
        <p className="text-sm text-ducens-muted mt-1">
          {client.company_name} · niche {client.niche}
        </p>
      </div>

      {kpisQuery.isPending && (
        <div className="flex items-center gap-2 text-ducens-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      )}

      {kpisQuery.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Impossible de récupérer les KPIs. Backend hors ligne ?
        </div>
      )}

      {kpisQuery.data && (
        <>
          {/* Action urgente : approvals à valider */}
          {kpisQuery.data.approvals_pending_count > 0 && (
            <Link
              to={`/clients/${slug}/approvals`}
              className="block mb-6 bg-ducens-accent/30 border border-ducens-accent rounded-lg p-4 hover:bg-ducens-accent/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">
                    {kpisQuery.data.approvals_pending_count} validation
                    {kpisQuery.data.approvals_pending_count > 1 ? 's' : ''} en attente
                  </div>
                  <div className="text-sm text-ducens-muted mt-0.5">
                    Outputs des agents en attente de ta validation
                  </div>
                </div>
                <ArrowRight className="w-5 h-5" />
              </div>
            </Link>
          )}

          <Section title="24 dernières heures" description="Variation par rapport à la veille">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <KpiCard
                label="CA jour"
                value={kpisQuery.data.ca_jour_eur.toFixed(0)}
                unit="€"
                variation={kpisQuery.data.ca_jour_variation}
                Icon={Euro}
              />
              <KpiCard
                label="Commandes"
                value={kpisQuery.data.orders_jour}
                Icon={ShoppingCart}
              />
              <KpiCard
                label="Leads (livret)"
                value={kpisQuery.data.leads_jour}
                Icon={UserPlus}
              />
              <KpiCard
                label="Reach 24h"
                value={kpisQuery.data.reach_24h.toLocaleString('fr-FR')}
                Icon={Eye}
              />
              <KpiCard
                label="Engagement"
                value={kpisQuery.data.engagement_median_pct.toFixed(1)}
                unit="%"
                Icon={Heart}
              />
              <KpiCard
                label="Budget IA"
                value={kpisQuery.data.anthropic_budget_used.toFixed(2)}
                unit={`/ ${kpisQuery.data.anthropic_budget_max} $`}
                Icon={BotIcon}
                state={
                  kpisQuery.data.anthropic_budget_used / kpisQuery.data.anthropic_budget_max > 0.8
                    ? 'warn'
                    : 'default'
                }
                helper="quotidien"
              />
            </div>
          </Section>

          <Section title="Raccourcis">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ActionCard
                to={`/clients/${slug}/content`}
                title="Pipeline contenus"
                description="Voir les reels en production et publication programmée"
              />
              <ActionCard
                to={`/clients/${slug}/agents`}
                title="État des agents"
                description="Activité et santé des agents IA des dernières 24h"
              />
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function ActionCard({ to, title, description }: { to: string; title: string; description: string }) {
  return (
    <Link
      to={to}
      className="block bg-white border border-ducens-border rounded-lg p-4 hover:border-ducens-ink transition-colors"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{title}</div>
          <div className="text-sm text-ducens-muted mt-0.5">{description}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-ducens-muted" />
      </div>
    </Link>
  );
}
